import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Chip, Collapse, Divider, Paper, Stack, Typography } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../widgets/Navbars/DashboardNavbar";
import { AppAutocomplete, AppButton, AppDateTimePicker, AppRadioGroup, AppTextField } from "../../../components/form";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../../../components/dialog";
import DateRangeHeader, { useDateRangeHeader } from "../../../components/DateRangeHeader";
import { AppFormField, AppFormGrid, PageCommandBar, ResponsiveActions } from "../../../components/layout";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import DashboardGrid from "../../../components/DashboardGrid";
import DashboardCrossFilterBar from "../../../components/DashboardCrossFilterBar";
import DrillDownDialog from "../../../components/DrillDownDialog";
import DashboardExportMenu from "../../../components/DashboardExportMenu";
import DashboardWidgetEditDialog from "../../../components/DashboardWidgetEditDialog";
import { ReportBuildPane, ReportDataPane, ReportPageStrip, ReportRibbon, useQuerySchemas } from "../../../components/ReportDesigner";
import DecorationEditDialog from "../../../components/DecorationEditDialog";
import VisualEditDialog from "../../../components/VisualEditDialog";
import { dashboardService, type DrillDownResponse } from "../../../services/dashboardService";
import { dashboardQueryService } from "../../../services/dashboardQueryService";
import { widgetService } from "../../../services/widgetService";
import type {
  DashboardBookmark,
  DashboardDecorationKind,
  DashboardDecorationPlacement,
  DashboardExecuteResponse,
  DashboardFilter,
  DashboardPage,
  DashboardVisualPlacement,
  DashboardWidgetPlacement,
  QueryColumnSchema,
  ReportDashboard,
} from "../../../types/dashboard";
import type { Widget } from "../../../types/widget";
import type { DashboardQuery } from "../../../types/query";
import type { CreateShareLinkRequest, ShareLink, ShareRuntimeMode } from "../../../types/share";
import { canExportDashboard, getCurrentUserRoles } from "../../../utils/authorization";
import { dashboardTimezone } from "../../../utils/dashboardDateDefaults";
import { toggleCrossFilter, type CrossFilter } from "../../../utils/crossFilter";
import { resolveGroupByFieldOrDefault } from "../../../utils/groupByField";
import { createDecoration, normalizeDecorations } from "../../../utils/decorationCatalog";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

type Mode = "view" | "edit";

function wellTarget(
  vizType: string | undefined,
  column: QueryColumnSchema,
  wells: DashboardVisualPlacement["wells"]
): { id: string; multiple: boolean; aggregate?: "sum" } {
  const measure = column.role === "measure" || column.type === "number";
  if (vizType === "table") return { id: "columns", multiple: true, aggregate: measure ? "sum" : undefined };
  if (vizType === "slicer") return { id: "field", multiple: false };
  if (vizType === "card" || vizType === "gauge") return { id: "values", multiple: false, aggregate: "sum" };
  if (vizType === "pie" || vizType === "treemap") {
    return { id: measure ? "values" : "legend", multiple: false, aggregate: measure ? "sum" : undefined };
  }
  if (vizType === "heatmap") {
    if (measure) return { id: "values", multiple: false, aggregate: "sum" };
    return { id: wells?.x?.length ? "y" : "x", multiple: false };
  }
  if (vizType === "sankey") {
    if (measure) return { id: "values", multiple: false, aggregate: "sum" };
    return { id: wells?.source?.length ? "target" : "source", multiple: false };
  }
  if (measure) return { id: vizType === "pie" ? "values" : "y", multiple: true, aggregate: "sum" };
  if (!wells?.x?.length) return { id: "x", multiple: false };
  return { id: "legend", multiple: false };
}

function vizForColumn(column: QueryColumnSchema): string {
  if (column.type === "date") return "line";
  if (column.role === "measure" || column.type === "number") return "card";
  return "column";
}

function syncActivePage(
  pages: DashboardPage[] | undefined,
  widgets: DashboardWidgetPlacement[],
  decorations: DashboardDecorationPlacement[],
  visuals: DashboardVisualPlacement[]
): DashboardPage[] | undefined {
  if (!pages?.length) return pages;
  const active = pages.find((page) => !page.hidden) || pages[0];
  return pages.map((page) =>
    page.id === active.id
      ? { ...page, widgets, decorations, visuals, hidden: false }
      : { ...page, hidden: true }
  );
}

function materializePages(
  dashboard: ReportDashboard,
  widgets: DashboardWidgetPlacement[],
  decorations: DashboardDecorationPlacement[],
  visuals: DashboardVisualPlacement[],
  translate: (key: string, options?: Record<string, unknown>) => string
): DashboardPage[] {
  if (dashboard.layout?.pages?.length) {
    return dashboard.layout.pages;
  }
  return [
    {
      id: crypto.randomUUID(),
      name: translate("reportDesigner.pageDefault", { index: 1 }),
      hidden: false,
      widgets,
      decorations,
      visuals,
    },
  ];
}

export default function DashboardDetailPage({ mode }: { mode: Mode }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [dashboard, setDashboard] = useState<ReportDashboard | null>(null);
  const [dateReady, setDateReady] = useState(false);
  const date = useDateRangeHeader();
  const [results, setResults] = useState<DashboardExecuteResponse | undefined>();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [queries, setQueries] = useState<DashboardQuery[]>([]);
  const [executing, setExecuting] = useState(false);
  const [loadingWidgetIds, setLoadingWidgetIds] = useState<Set<string>>(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [shareForm, setShareForm] = useState<CreateShareLinkRequest>({
    mode: "PUBLIC",
    runtimeMode: "LIVE",
  });
  const [shareExpiresAt, setShareExpiresAt] = useState<dayjs.Dayjs | null>(null);
  const [shareSnapshotFrom, setShareSnapshotFrom] = useState<dayjs.Dayjs | null>(null);
  const [shareSnapshotTo, setShareSnapshotTo] = useState<dayjs.Dayjs | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [lastShareUrl, setLastShareUrl] = useState("");
  const [widgetEditDialog, setWidgetEditDialog] = useState<{
    widgetId: string;
    titleOverride: string;
  } | null>(null);
  const [decorationEditDialog, setDecorationEditDialog] = useState<DashboardDecorationPlacement | null>(null);
  const [visualEditDialog, setVisualEditDialog] = useState<DashboardVisualPlacement | null>(null);
  const [visualEditInitialTab, setVisualEditInitialTab] = useState(0);
  const [exportAllowed, setExportAllowed] = useState(false);
  const [crossFilters, setCrossFilters] = useState<CrossFilter[]>([]);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownData, setDrillDownData] = useState<DrillDownResponse | null>(null);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [selectedVisualId, setSelectedVisualId] = useState<string>();
  const [buildFocusToken, setBuildFocusToken] = useState(0);
  const [selectedQueryId, setSelectedQueryId] = useState<string>();
  const [zoom, setZoom] = useState(1);
  const [historyCount, setHistoryCount] = useState(0);
  const [futureCount, setFutureCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const historyRef = useRef<string[]>([]);
  const futureRef = useRef<string[]>([]);

  const placements: DashboardWidgetPlacement[] = dashboard?.layout?.widgets || [];
  const decorations: DashboardDecorationPlacement[] = dashboard?.layout?.decorations || [];
  const visuals: DashboardVisualPlacement[] = dashboard?.layout?.visuals || [];
  const selectedVisual = visuals.find((visual) => visual.id === selectedVisualId);
  const assignedQueryIds = dashboard?.assignedQueryIds || [];
  const assignedQueries = useMemo(
    () => queries.filter((query) => query.id && assignedQueryIds.includes(query.id)),
    [queries, assignedQueryIds]
  );
  const schemaQueries = useMemo(() => {
    const assigned = queries.filter((query) => query.id && assignedQueryIds.includes(query.id));
    return assigned.length ? assigned : queries;
  }, [queries, assignedQueryIds]);
  const schemasByQueryId = useQuerySchemas(schemaQueries);
  const schemaColumns = schemasByQueryId[selectedQueryId || ""]?.columns || [];
  const placedWidgetIds = useMemo(() => new Set(placements.map((p) => p.widgetId)), [placements]);
  const pages = dashboard?.layout?.pages || [];
  const shareRuntimeOptions = useMemo(
    () =>
      [
        { value: "LIVE" as ShareRuntimeMode, label: t("share.live") },
        { value: "SNAPSHOT" as ShareRuntimeMode, label: t("share.snapshot") },
      ] as const,
    [t]
  );

  const selectedShareRuntime =
    shareRuntimeOptions.find((o) => o.value === shareForm.runtimeMode) ?? shareRuntimeOptions[0];

  const shareSnapshotValid = useMemo(() => {
    if (shareForm.runtimeMode !== "SNAPSHOT") return true;
    if (!shareSnapshotFrom || !shareSnapshotTo) return false;
    return !shareSnapshotFrom.isAfter(shareSnapshotTo);
  }, [shareForm.runtimeMode, shareSnapshotFrom, shareSnapshotTo]);

  const widgetNameById = useMemo(() => {
    const map = new Map<string, string>();
    widgets.forEach((w) => {
      if (w.id) map.set(w.id, w.name || w.id);
    });
    return map;
  }, [widgets]);

  const widgetById = useMemo(() => {
    const map = new Map<string, Widget>();
    widgets.forEach((w) => {
      if (w.id) map.set(w.id, w);
    });
    return map;
  }, [widgets]);

  const load = useCallback(async () => {
    const d = await dashboardService.get(id);
    const normalizedDecorations = normalizeDecorations(d.layout?.decorations);
    if (normalizedDecorations.length > 0) {
      d.layout = { ...d.layout, decorations: normalizedDecorations };
    }
    setDashboard(d);
    setDirty(false);
    historyRef.current = [];
    futureRef.current = [];
    setHistoryCount(0);
    setFutureCount(0);
    date.applyDashboardDefaults(d.defaults);
    setDateReady(true);
    const w = await widgetService.list(0, 200);
    setWidgets(
      (w.content || []).filter(
        (item) => item.status === "ACTIVE" || item.status === undefined || item.status === null
      )
    );
    const q = await dashboardQueryService.list(0, 200);
    setQueries(q.content || []);
  }, [id]);

  const execute = useCallback(async () => {
    const ids = placements.map((p) => p.widgetId);
    const { from, to } = date.range;
    setExecuting(true);
    setLoadingWidgetIds(new Set(ids));
    try {
      const res = await dashboardService.execute(id, {
        from,
        to,
        timezone: dashboardTimezone(dashboard?.defaults),
        crossFilters: crossFilters.map((f) => ({
          field: f.field,
          value: f.value,
          sourceWidgetId: f.sourceWidgetId,
        })),
      });
      setResults(res);
    } finally {
      setExecuting(false);
      setLoadingWidgetIds(new Set());
    }
  }, [id, date.range.from, date.range.to, placements, crossFilters, dashboard?.defaults]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useEffect(() => {
    getCurrentUserRoles()
      .then((roles) => setExportAllowed(canExportDashboard(roles)))
      .catch(() => setExportAllowed(false));
  }, []);

  const executeRef = useRef(execute);
  executeRef.current = execute;

  useEffect(() => {
    if (!dashboard || !dateReady) return undefined;
    const timer = window.setTimeout(() => {
      executeRef.current().catch(console.error);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [dashboard?.id, dateReady, date.range.from, date.range.to, crossFilters, refreshTick]);

  const handleChartClick = useCallback(
    async (widgetId: string, categoryValue: string) => {
      const widget = widgetById.get(widgetId);
      const field = resolveGroupByFieldOrDefault(widget);
      const next: CrossFilter = {
        field,
        value: categoryValue,
        sourceWidgetId: widgetId,
        label: categoryValue,
      };
      setCrossFilters((prev) => toggleCrossFilter(prev, next));

      setDrillDownOpen(true);
      setDrillDownLoading(true);
      setDrillDownData(null);
      try {
        const data = await dashboardService.drillDown(id, {
          field,
          value: categoryValue,
          from: date.range.from,
          to: date.range.to,
          index: widget?.dataSource?.indices?.[0],
        });
        setDrillDownData(data);
      } catch (e) {
        console.error(e);
      } finally {
        setDrillDownLoading(false);
      }
    },
    [widgetById, id, date.range.from, date.range.to]
  );

  useEffect(() => {
    if (selectedQueryId) return;
    const first = assignedQueryIds[0] || queries.find((query) => query.id)?.id;
    if (first) setSelectedQueryId(first);
  }, [assignedQueryIds, queries, selectedQueryId]);

  const snapshotOf = (value: ReportDashboard) =>
    JSON.stringify({
      layout: value.layout,
      filters: value.filters,
      bookmarks: value.bookmarks,
      assignedQueryIds: value.assignedQueryIds,
    });

  const remember = () => {
    if (!dashboard) return;
    historyRef.current.push(snapshotOf(dashboard));
    if (historyRef.current.length > 40) historyRef.current.shift();
    futureRef.current = [];
    setHistoryCount(historyRef.current.length);
    setFutureCount(0);
  };

  /** Local draft only — nothing is persisted until Save. */
  const applyDraft = (next: ReportDashboard, record = true) => {
    if (record) remember();
    setDashboard(next);
    setDirty(true);
  };

  const persistChanges = async () => {
    if (!dashboard || !dirty) return;
    setSaving(true);
    try {
      const saved = await dashboardService.update(id, dashboard);
      setDashboard(saved);
      setDirty(false);
      historyRef.current = [];
      futureRef.current = [];
      setHistoryCount(0);
      setFutureCount(0);
      setRefreshTick((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const saveFullLayout = (
    widgets: DashboardWidgetPlacement[],
    nextDecorations: DashboardDecorationPlacement[] = decorations,
    nextAssignedQueryIds: string[] = assignedQueryIds,
    nextVisuals: DashboardVisualPlacement[] = visuals
  ) => {
    if (!dashboard) return;
    const nextPages = syncActivePage(dashboard.layout?.pages, widgets, nextDecorations, nextVisuals);
    applyDraft({
      ...dashboard,
      assignedQueryIds: nextAssignedQueryIds,
      layout: {
        ...dashboard.layout,
        widgets,
        decorations: nextDecorations,
        visuals: nextVisuals,
        pages: nextPages,
      },
    });
  };

  const assignQuery = (queryId: string) => {
    if (assignedQueryIds.includes(queryId)) return;
    saveFullLayout(placements, decorations, [...assignedQueryIds, queryId]);
  };

  const unassignQuery = (queryId: string) => {
    const next = assignedQueryIds.filter((existingId) => existingId !== queryId);
    saveFullLayout(placements, decorations, next);
  };

  const startRename = () => {
    setNameDraft(dashboard?.name || "");
    setNameEditing(true);
  };

  const cancelRename = () => {
    setNameEditing(false);
    setNameDraft("");
  };

  const saveRename = async () => {
    if (!dashboard) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) return;
    if (trimmed === dashboard.name) {
      cancelRename();
      return;
    }
    setNameSaving(true);
    try {
      const saved = await dashboardService.update(id, { ...dashboard, name: trimmed });
      setDashboard(saved);
      cancelRename();
    } catch (e) {
      console.error(e);
    } finally {
      setNameSaving(false);
    }
  };

  const saveLayout = (next: DashboardWidgetPlacement[]) => {
    saveFullLayout(next);
  };

  const notifyDuplicate = () => {
    window.dispatchEvent(
      new CustomEvent("showGlobalNotification", {
        detail: {
          color: "warning",
          icon: "info",
          title: t("dashboard.addWidget"),
          content: t("dashboard.duplicateWidget"),
        },
      })
    );
  };

  const addWidget = (
    widgetId: string,
    position?: { x: number; y: number; w: number; h: number }
  ) => {
    if (placedWidgetIds.has(widgetId)) {
      notifyDuplicate();
      return;
    }
    const next: DashboardWidgetPlacement[] = [
      ...placements,
      {
        widgetId,
        x: position?.x ?? 0,
        y: position?.y ?? Infinity,
        w: position?.w ?? 6,
        h: position?.h ?? 4,
        minW: 3,
        minH: 2,
      },
    ];
    saveLayout(next);
  };

  const removeWidget = async (widgetId: string) => {
    try {
      const placement = placements.find((p) => p.widgetId === widgetId);
      const itemName =
        placement?.titleOverride?.trim() ||
        widgetNameById.get(widgetId) ||
        widgetId;
      await confirmDelete({
        itemName,
        titleKey: "confirm.removeWidgetTitle",
        descriptionKey: "confirm.removeWidgetDescription",
      });
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }

    const next = placements.filter((p) => p.widgetId !== widgetId);
    saveLayout(next);
    setResults((prev) =>
      prev
        ? {
            ...prev,
            widgets: prev.widgets?.filter((w) => w.widgetId !== widgetId),
          }
        : prev
    );
  };

  const handleWidgetEditSaved = (widgetId: string, titleOverride?: string) => {
    const next = placements.map((p) =>
      p.widgetId === widgetId ? { ...p, titleOverride } : p
    );
    saveLayout(next);
  };

  const addDecoration = (
    kind: DashboardDecorationKind,
    position?: { x: number; y: number; w: number; h: number }
  ) => {
    const item = createDecoration(kind, position);
    const next = [...decorations, item];
    saveFullLayout(placements, next);
    setDecorationEditDialog(item);
  };

  const removeDecoration = async (decorationId: string) => {
    try {
      await confirmDelete({
        titleKey: "confirm.removeDecorationTitle",
        descriptionKey: "confirm.removeDecorationDescription",
      });
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }

    const next = decorations.filter((d) => d.id !== decorationId);
    saveFullLayout(placements, next);
  };

  const handleDecorationSaved = (item: DashboardDecorationPlacement) => {
    const next = decorations.map((d) => (d.id === item.id ? item : d));
    saveFullLayout(placements, next);
  };

  const putVisuals = (next: DashboardVisualPlacement[], assigned = assignedQueryIds, record = true) => {
    if (!dashboard) return;
    if (record) remember();
    const nextPages = syncActivePage(
      dashboard.layout?.pages,
      dashboard.layout?.widgets || placements,
      dashboard.layout?.decorations || decorations,
      next
    );
    setDashboard({
      ...dashboard,
      assignedQueryIds: assigned,
      layout: { ...dashboard.layout, visuals: next, pages: nextPages },
    });
    setDirty(true);
  };

  const previewVisualResult = useCallback(
    async (visual: DashboardVisualPlacement) => {
      if (!visual?.id || !id) return;
      try {
        const preview = await dashboardService.previewVisual(id, visual, {
          from: date.range.from,
          to: date.range.to,
          timezone: dashboardTimezone(dashboard?.defaults),
          crossFilters: crossFilters.map((filter) => ({
            field: filter.field,
            value: filter.value,
            sourceWidgetId: filter.sourceWidgetId,
          })),
        });
        setResults((prev) => {
          const nextVisuals = [...(prev?.visuals || [])];
          const index = nextVisuals.findIndex((item) => item.visualId === visual.id);
          if (index >= 0) nextVisuals[index] = preview;
          else nextVisuals.push(preview);
          return {
            widgets: prev?.widgets || [],
            dataQueries: prev?.dataQueries,
            visuals: nextVisuals,
          };
        });
      } catch (error) {
        console.error(error);
      }
    },
    [id, date.range.from, date.range.to, dashboard?.defaults, crossFilters]
  );

  const addVisual = (
    vizType: string,
    column?: QueryColumnSchema,
    position?: { x: number; y: number; w: number; h: number },
    sourceQueryId?: string
  ) => {
    const queryId = sourceQueryId || selectedQueryId || assignedQueryIds[0];
    const defaultW = vizType === "card" || vizType === "slicer" ? 3 : 6;
    const defaultH = vizType === "card" ? 2 : 4;
    const visual: DashboardVisualPlacement = {
      id: crypto.randomUUID(),
      queryId,
      vizType,
      title: vizType,
      x: position?.x ?? 0,
      y: position?.y ?? 0,
      w: position?.w ?? defaultW,
      h: position?.h ?? defaultH,
      minW: 2,
      minH: 2,
      wells: {},
      z: visuals.length + 1,
    };
    if (column) {
      const target = wellTarget(vizType, column, visual.wells);
      visual.wells = {
        [target.id]: [{ field: column.name, aggregate: target.aggregate }],
      };
    }
    if (position?.y == null) {
      const maxY = Math.max(
        0,
        ...placements.map((item) => item.y + item.h),
        ...decorations.map((item) => item.y + item.h),
        ...visuals.map((item) => item.y + item.h)
      );
      visual.y = maxY;
    }
    setSelectedVisualId(visual.id);
    setBuildFocusToken((value) => value + 1);
    const assigned = queryId && !assignedQueryIds.includes(queryId) ? [...assignedQueryIds, queryId] : assignedQueryIds;
    putVisuals([...visuals, visual], assigned);
    void previewVisualResult(visual);
  };

  const changeVisual = (visual: DashboardVisualPlacement, opts?: { preview?: boolean }) => {
    if (!dashboard) return;
    const currentVisuals = dashboard.layout?.visuals || visuals;
    const next = currentVisuals.map((item) => (item.id === visual.id ? visual : item));
    putVisuals(next);
    if (opts?.preview !== false) {
      void previewVisualResult(visual);
    }
  };

  const editVisual = (visualId: string, options?: { tab?: number }) => {
    const visual = visuals.find((item) => item.id === visualId);
    if (!visual) return;
    setSelectedVisualId(visualId);
    if (visual.queryId) setSelectedQueryId(visual.queryId);
    setBuildFocusToken((value) => value + 1);
    setVisualEditInitialTab(options?.tab ?? 0);
    setVisualEditDialog(structuredClone(visual));
  };

  const handleVisualEditApplied = (visual: DashboardVisualPlacement) => {
    const next = structuredClone(visual);
    setVisualEditDialog(null);
    changeVisual(next);
  };

  const removeVisual = (visualId: string) => {
    putVisuals(visuals.filter((item) => item.id !== visualId));
    if (selectedVisualId === visualId) setSelectedVisualId(undefined);
  };

  const exploreObject = async (queryId: string, objectName: string) => {
    const query = queries.find((item) => item.id === queryId);
    if (!query?.id) return;
    const saved = await dashboardQueryService.update(query.id, { ...query, objectName });
    setQueries((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
  };

  const useField = (column: QueryColumnSchema, queryId: string) => {
    setSelectedQueryId(queryId);
    if (queryId && !assignedQueryIds.includes(queryId)) {
      void assignQuery(queryId);
    }
    if (!selectedVisual) {
      addVisual(vizForColumn(column), column, undefined, queryId);
      return;
    }
    const switching = !!selectedVisual.queryId && selectedVisual.queryId !== queryId;
    const baseWells = switching ? {} : selectedVisual.wells;
    const target = wellTarget(selectedVisual.vizType, column, baseWells);
    const current = baseWells?.[target.id] || [];
    const field = { field: column.name, aggregate: target.aggregate };
    const nextFields = target.multiple ? [...current, field] : [field];
    changeVisual({
      ...selectedVisual,
      queryId,
      wells: { ...baseWells, [target.id]: nextFields },
    });
  };

  const addScopedFilter = (scope: "visual" | "page" | "report", filter: DashboardFilter) => {
    if (!dashboard || !filter.field) return;
    const withId = { ...filter, id: crypto.randomUUID() };
    if (scope === "report") {
      applyDraft({ ...dashboard, filters: [...(dashboard.filters || []), withId] });
      return;
    }
    if (scope === "visual" && selectedVisual) {
      changeVisual({ ...selectedVisual, filters: [...(selectedVisual.filters || []), withId] });
      return;
    }
    const basePages = materializePages(dashboard, placements, decorations, visuals, t);
    const active = basePages.find((page) => !page.hidden) || basePages[0];
    const nextPages = basePages.map((page) =>
      page.id === active.id
        ? { ...page, widgets: placements, decorations, visuals, hidden: false, filters: [...(page.filters || []), withId] }
        : { ...page, hidden: true }
    );
    applyDraft({ ...dashboard, layout: { ...dashboard.layout, pages: nextPages, widgets: placements, decorations, visuals } });
  };

  const saveBookmark = (name: string) => {
    if (!dashboard || !name.trim()) return;
    const active = pages.find((page) => !page.hidden);
    const bookmark: DashboardBookmark = {
      id: crypto.randomUUID(),
      name: name.trim(),
      pageId: active?.id,
      filters: [...(dashboard.filters || []), ...(active?.filters || [])],
    };
    applyDraft({ ...dashboard, bookmarks: [...(dashboard.bookmarks || []), bookmark] });
  };

  const applyBookmark = (bookmark: DashboardBookmark) => {
    if (!dashboard) return;
    const nextPages = pages.map((page) =>
      !bookmark.pageId || page.id === bookmark.pageId
        ? { ...page, filters: bookmark.filters || [], hidden: bookmark.pageId ? page.id !== bookmark.pageId : page.hidden }
        : { ...page, hidden: true }
    );
    applyDraft({
      ...dashboard,
      filters: bookmark.filters || [],
      layout: pages.length ? { ...dashboard.layout, pages: nextPages } : dashboard.layout,
    });
  };

  const restoreHistory = (raw: string) => {
    if (!dashboard) return;
    const parsed = JSON.parse(raw) as Pick<ReportDashboard, "layout" | "filters" | "bookmarks" | "assignedQueryIds">;
    setDashboard({ ...dashboard, ...parsed });
    setDirty(true);
  };

  const undo = () => {
    const prev = historyRef.current.pop();
    if (!prev || !dashboard) return;
    futureRef.current.push(snapshotOf(dashboard));
    setHistoryCount(historyRef.current.length);
    setFutureCount(futureRef.current.length);
    restoreHistory(prev);
  };

  const redo = () => {
    const next = futureRef.current.pop();
    if (!next || !dashboard) return;
    historyRef.current.push(snapshotOf(dashboard));
    setHistoryCount(historyRef.current.length);
    setFutureCount(futureRef.current.length);
    restoreHistory(next);
  };

  const alignVisual = (edge: "left" | "center" | "top") => {
    if (!selectedVisual) return;
    const cols = dashboard?.layout?.gridCols ?? 12;
    changeVisual({
      ...selectedVisual,
      x: edge === "left" ? 0 : edge === "center" ? Math.max(0, Math.round((cols - selectedVisual.w) / 2)) : selectedVisual.x,
      y: edge === "top" ? 0 : selectedVisual.y,
    });
  };

  const orderVisual = (front: boolean) => {
    if (!selectedVisual) return;
    const zs = visuals.map((item) => item.z || 0);
    const z = front ? Math.max(0, ...zs) + 1 : Math.min(0, ...zs) - 1;
    changeVisual({ ...selectedVisual, z });
  };

  const duplicateVisual = () => {
    if (!selectedVisual?.id) return;
    const copy: DashboardVisualPlacement = {
      ...selectedVisual,
      id: crypto.randomUUID(),
      x: Math.min((dashboard?.layout?.gridCols ?? 12) - selectedVisual.w, selectedVisual.x + 1),
      y: selectedVisual.y + 1,
      title: selectedVisual.title,
    };
    setSelectedVisualId(copy.id);
    putVisuals([...visuals, copy]);
  };

  const selectPage = (pageId: string) => {
    if (!dashboard) return;
    const base = materializePages(dashboard, placements, decorations, visuals, t);
    const active = base.find((page) => !page.hidden) || base[0];
    if (active.id === pageId || pageId === "page-default") return;
    const stored = base.map((page) =>
      page.id === active.id ? { ...page, widgets: placements, decorations, visuals } : page
    );
    const nextPages = stored.map((page) => ({ ...page, hidden: page.id !== pageId }));
    const next = nextPages.find((page) => page.id === pageId);
    const updated: ReportDashboard = {
      ...dashboard,
      layout: {
        ...dashboard.layout,
        pages: nextPages,
        widgets: next?.widgets || [],
        decorations: next?.decorations || [],
        visuals: next?.visuals || [],
      },
    };
    if (mode === "edit") {
      applyDraft(updated);
    } else {
      setDashboard(updated);
    }
    setSelectedVisualId(undefined);
  };

  const addPage = () => {
    if (!dashboard) return;
    const stamped = materializePages(dashboard, placements, decorations, visuals, t).map((page, _index, all) => {
      const active = all.find((item) => !item.hidden) || all[0];
      return page.id === active.id
        ? { ...page, widgets: placements, decorations, visuals, hidden: true }
        : { ...page, hidden: true };
    });
    const created: DashboardPage = {
      id: crypto.randomUUID(),
      name: t("reportDesigner.pageDefault", { index: stamped.length + 1 }),
      hidden: false,
      widgets: [],
      decorations: [],
      visuals: [],
    };
    applyDraft({
      ...dashboard,
      layout: {
        ...dashboard.layout,
        pages: [...stamped, created],
        widgets: [],
        decorations: [],
        visuals: [],
      },
    });
    setSelectedVisualId(undefined);
  };

  const renamePage = (pageId: string, name: string) => {
    if (!dashboard || !name.trim()) return;
    const base = materializePages(dashboard, placements, decorations, visuals, t);
    const targetId =
      pageId === "page-default" ? (base.find((page) => !page.hidden) || base[0]).id : pageId;
    applyDraft({
      ...dashboard,
      layout: {
        ...dashboard.layout,
        pages: base.map((page) => (page.id === targetId ? { ...page, name: name.trim() } : page)),
        widgets: placements,
        decorations,
        visuals,
      },
    });
  };

  const removePage = (pageId: string) => {
    if (!dashboard) return;
    const base = materializePages(dashboard, placements, decorations, visuals, t);
    if (base.length <= 1) return;
    const active = base.find((page) => !page.hidden) || base[0];
    const stamped = base.map((page) =>
      page.id === active.id ? { ...page, widgets: placements, decorations, visuals } : page
    );
    const remaining = stamped.filter((page) => page.id !== pageId);
    const nextActive = remaining.find((page) => page.id === active.id) || remaining[0];
    applyDraft({
      ...dashboard,
      layout: {
        ...dashboard.layout,
        pages: remaining.map((page) => ({ ...page, hidden: page.id !== nextActive.id })),
        widgets: nextActive.widgets || [],
        decorations: nextActive.decorations || [],
        visuals: nextActive.visuals || [],
      },
    });
    setSelectedVisualId(undefined);
  };

  const handleVisualChartClick = (visualId: string, categoryValue: string) => {
    const visual = visuals.find((item) => item.id === visualId);
    const field = visual?.wells?.x?.[0]?.field
      || visual?.wells?.legend?.[0]?.field
      || visual?.wells?.field?.[0]?.field
      || visual?.wells?.source?.[0]?.field;
    if (!field) return;
    if (visual?.vizType === "slicer") {
      addScopedFilter("page", { field, operator: "eq", values: [categoryValue] });
      return;
    }
    setCrossFilters((prev) => toggleCrossFilter(prev, {
      field,
      value: categoryValue,
      sourceWidgetId: visualId,
      label: categoryValue,
    }));
  };

  const openShare = async () => {
    setShareError(null);
    setShareSnapshotFrom(dayjs(date.range.from));
    setShareSnapshotTo(dayjs(date.range.to));
    setShareOpen(true);
    const links = await dashboardService.listShareLinks(id);
    setShareLinks(links);
  };

  const revokeShareLink = async (token: string) => {
    try {
      await confirmDelete({
        titleKey: "confirm.revokeShareTitle",
        descriptionKey: "confirm.revokeShareDescription",
      });
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }

    await dashboardService.revokeShareLink(token);
    await openShare();
  };

  const createShare = async () => {
    setShareError(null);
    const body: CreateShareLinkRequest = { ...shareForm };
    if (body.runtimeMode === "SNAPSHOT") {
      if (!shareSnapshotFrom || !shareSnapshotTo) {
        setShareError(t("share.snapshotRangeRequired"));
        return;
      }
      if (shareSnapshotFrom.isAfter(shareSnapshotTo)) {
        setShareError(t("share.snapshotRangeInvalid"));
        return;
      }
      body.snapshotFrom = shareSnapshotFrom.toDate().toISOString();
      body.snapshotTo = shareSnapshotTo.toDate().toISOString();
    }
    if (shareExpiresAt) {
      body.expiresAt = shareExpiresAt.toDate().toISOString();
    }
    const link = await dashboardService.createShareLink(id, body);
    const url = `${window.location.origin}/s/${link.token}`;
    setLastShareUrl(url);
    setShareLinks(await dashboardService.listShareLinks(id));
  };

  const copyShareUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: { color: "success", icon: "link", title: t("share.copyLink"), content: t("share.copySuccess") },
        })
      );
    } catch {
      /* ignore */
    }
  };

  const editable = mode === "edit";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={custom.pages.dashboards.detailRoot}
      >
        <Box className="no-print">
          <PageCommandBar
            leading={
              <AppIconButton
                title={t("dashboard.title")}
                onClick={() => navigate("/dashboards")}
              >
                <ArrowBackOutlinedIcon fontSize="small" />
              </AppIconButton>
            }
            title={
              nameEditing
                ? nameDraft || t("dashboard.editor")
                : dashboard?.name || t("dashboard.editor")
            }
            subtitle={
              nameEditing ? (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <AppTextField
                    size="small"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveRename().catch(console.error);
                      }
                      if (e.key === "Escape") cancelRename();
                    }}
                    disabled={nameSaving}
                    autoFocus
                    sx={custom.pages.dashboards.renameField}
                  />
                  <AppIconButton
                    size="small"
                    color="success"
                    aria-label={t("common.save")}
                    disabled={nameSaving || !nameDraft.trim()}
                    onClick={() => saveRename().catch(console.error)}
                  >
                    <CheckOutlinedIcon fontSize="small" />
                  </AppIconButton>
                  <AppIconButton
                    size="small"
                    aria-label={t("common.cancel")}
                    disabled={nameSaving}
                    onClick={cancelRename}
                  >
                    <CloseOutlinedIcon fontSize="small" />
                  </AppIconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {dashboard?.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {dashboard.description}
                    </Typography>
                  )}
                  <AppIconButton
                    size="small"
                    aria-label={t("dashboard.rename")}
                    onClick={startRename}
                  >
                    <DriveFileRenameOutlineOutlinedIcon fontSize="small" />
                  </AppIconButton>
                  <Chip
                    size="small"
                    label={editable ? t("dashboard.editMode") : t("dashboard.viewMode")}
                    color={editable ? "warning" : "default"}
                    variant="outlined"
                    sx={custom.pages.dashboards.modeChip}
                  />
                  {editable && dirty && (
                    <Chip
                      size="small"
                      label={t("reportDesigner.unsaved")}
                      color="warning"
                      variant="filled"
                    />
                  )}
                  {placements.length > 0 && (
                    <Chip
                      size="small"
                      label={t("dashboard.widgetCount", { count: placements.length })}
                      variant="outlined"
                    />
                  )}
                  {decorations.length > 0 && (
                    <Chip
                      size="small"
                      label={t("dashboard.decor.count", { count: decorations.length })}
                      variant="outlined"
                      color="secondary"
                    />
                  )}
                </Stack>
              )
            }
            actions={
              <ResponsiveActions
                items={[
                  ...(editable
                    ? [
                        {
                          key: "save",
                          label: t("common.save"),
                          icon: "save",
                          primary: true,
                          color: "success" as const,
                          disabled: !dirty || saving,
                          onClick: () => {
                            void persistChanges().catch(console.error);
                          },
                        },
                        {
                          key: "view",
                          label: t("dashboard.view"),
                          icon: "visibility",
                          onClick: () => navigate(`/dashboards/${id}`),
                        },
                      ]
                    : [
                        {
                          key: "edit",
                          label: t("common.edit"),
                          icon: "edit",
                          primary: true,
                          onClick: () => navigate(`/dashboards/${id}/edit`),
                        },
                      ]),
                  {
                    key: "share",
                    label: t("common.share"),
                    icon: "share",
                    primary: !editable,
                    onClick: openShare,
                  },
                  ...(exportAllowed
                    ? [
                        {
                          key: "export",
                          label: t("common.export"),
                          onClick: () => {},
                          render: () => (
                            <DashboardExportMenu
                              dashboardId={id}
                              dashboardName={dashboard?.name}
                              dateRange={date.range}
                              disabled={executing || (placements.length === 0 && visuals.length === 0)}
                            />
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            }
          />
        </Box>

        <Box id="dashboard-print-area" className="dashboard-print-area" sx={custom.pages.dashboards.printArea}>
          <Paper
            variant="outlined"
            className="no-print"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={() => setFiltersExpanded((value) => !value)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <FilterAltOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                {t("dashboard.filters")}
              </Typography>
              {!filtersExpanded && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "45%" }}>
                  {date.label}
                  {crossFilters.length
                    ? ` · ${t("dashboard.crossFilterCount", { count: crossFilters.length })}`
                    : ""}
                </Typography>
              )}
              {filtersExpanded ? (
                <ExpandLessIcon fontSize="small" color="action" />
              ) : (
                <ExpandMoreIcon fontSize="small" color="action" />
              )}
            </Stack>
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                <DateRangeHeader
                  showYearMonthChips
                  selectedYear={date.selectedYear}
                  availableYears={date.availableYears}
                  onYearSelect={date.applyYear}
                  yearMonthChips={date.yearMonthChips}
                  activeYearMonth={date.activeYearMonth}
                  onYearMonthSelect={date.applyYearMonth}
                  range={date.range}
                  preset={date.preset}
                  onPresetChange={date.setPreset}
                  customFrom={date.customFrom}
                  customTo={date.customTo}
                  onCustomFromChange={date.setCustomFrom}
                  onCustomToChange={date.setCustomTo}
                  onApplyCustom={date.applyCustom}
                  label={date.label}
                  onRefresh={execute}
                  executing={executing}
                />
                <DashboardCrossFilterBar
                  filters={crossFilters}
                  onRemove={(filter) =>
                    setCrossFilters((prev) =>
                      prev.filter((f) => f.field !== filter.field || f.value !== filter.value)
                    )
                  }
                  onClearAll={() => setCrossFilters([])}
                />
              </Box>
            </Collapse>
          </Paper>

        <Box sx={custom.pages.dashboards.gridSection}>
          {editable && (
            <ReportRibbon
              canUndo={historyCount > 0}
              canRedo={futureCount > 0}
              onUndo={undo}
              onRedo={redo}
              onZoomIn={() => setZoom((value) => Math.min(1.6, Number((value + 0.1).toFixed(2))))}
              onZoomOut={() => setZoom((value) => Math.max(0.6, Number((value - 0.1).toFixed(2))))}
              onFit={() => setZoom(1)}
              onAlignLeft={() => alignVisual("left")}
              onAlignCenter={() => alignVisual("center")}
              onAlignTop={() => alignVisual("top")}
              onFront={() => orderVisual(true)}
              onBack={() => orderVisual(false)}
              onDuplicate={duplicateVisual}
            />
          )}
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems="stretch"
            sx={custom.pages.dashboards.gridStack}
          >
            {editable && (
              <ReportDataPane
                queries={queries}
                assignedQueryIds={assignedQueryIds}
                selectedQueryId={selectedQueryId}
                visualQueryId={selectedVisual?.queryId}
                onSelectQuery={setSelectedQueryId}
                schemasByQueryId={schemasByQueryId}
                onUseField={useField}
                onExplore={(queryId, objectName) => exploreObject(queryId, objectName).catch(console.error)}
                widgets={widgets}
                placedWidgetIds={placedWidgetIds}
                onAddWidget={(widgetId) => addWidget(widgetId)}
                onAddDecoration={(kind) => addDecoration(kind)}
                decorations={decorations}
                onAssignQuery={assignQuery}
                onUnassignQuery={unassignQuery}
              />
            )}
            <Box
              id="dashboard-export-surface"
              sx={{ ...custom.pages.dashboards.gridContainer, zoom, minWidth: 0, flex: 1 }}
            >
            <DashboardGrid
              placements={placements}
              decorations={decorations}
              visuals={visuals}
              results={results}
              dataQueries={results?.dataQueries}
              editable={editable}
              rowHeight={dashboard?.layout?.rowHeight || 80}
              gridCols={dashboard?.layout?.gridCols ?? 12}
              zones={dashboard?.layout?.zones}
              loadingWidgetIds={loadingWidgetIds}
              globalLoading={executing}
              mobileStacked={!!dashboard?.layout?.mobileStacked}
              selectedVisualId={selectedVisualId}
              onVisualSelect={setSelectedVisualId}
              onVisualEdit={editVisual}
              onRemoveVisual={removeVisual}
              onVisualChartClick={handleVisualChartClick}
              onGridLayoutChange={({ widgets, decorations: nextDecorations, visuals: nextVisuals }) =>
                saveFullLayout(widgets, nextDecorations, assignedQueryIds, nextVisuals)
              }
              onWidgetDrop={(widgetId, position) => addWidget(widgetId, position)}
              onDecorationDrop={(kind, position) =>
                addDecoration(kind as DashboardDecorationKind, position)
              }
              onVisualDrop={(vizType, position) => addVisual(vizType, undefined, position)}
              onRemoveWidget={removeWidget}
              onRemoveDecoration={removeDecoration}
              onEditWidget={(widgetId, currentTitle) =>
                setWidgetEditDialog({ widgetId, titleOverride: currentTitle || "" })
              }
              onEditDecoration={(item) => setDecorationEditDialog(item)}
              widgetNameById={widgetNameById}
              onChartClick={handleChartClick}
            />
            <ReportPageStrip
              pages={
                pages.length
                  ? pages
                  : [
                      {
                        id: "page-default",
                        name: t("reportDesigner.pageDefault", { index: 1 }),
                        hidden: false,
                        widgets: placements,
                        decorations,
                        visuals,
                      },
                    ]
              }
              editable={editable}
              onSelect={selectPage}
              onAdd={addPage}
              onRename={renamePage}
              onRemove={removePage}
            />
            </Box>

            {editable && dashboard && (
              <ReportBuildPane
                dashboard={dashboard}
                visual={selectedVisual}
                focusToken={buildFocusToken}
                pages={pages.map((page) => ({ id: page.id || "", name: page.name || "" }))}
                onAddVisual={addVisual}
                onChangeVisual={changeVisual}
                onEditVisual={editVisual}
                onAddFilter={addScopedFilter}
                onSaveBookmark={saveBookmark}
                onApplyBookmark={applyBookmark}
                mobileStacked={!!dashboard.layout?.mobileStacked}
                onToggleMobile={() =>
                  applyDraft({
                    ...dashboard,
                    layout: { ...dashboard.layout, mobileStacked: !dashboard.layout?.mobileStacked },
                  })
                }
              />
            )}
          </Stack>
        </Box>
        </Box>
      </Box>

      <VisualEditDialog
        open={!!visualEditDialog}
        visual={visualEditDialog}
        columns={schemaColumns}
        schemasByQueryId={schemasByQueryId}
        queries={assignedQueries.length ? assignedQueries : queries}
        initialTab={visualEditInitialTab}
        onClose={() => setVisualEditDialog(null)}
        onApply={handleVisualEditApplied}
      />

      <DecorationEditDialog
        open={!!decorationEditDialog}
        item={decorationEditDialog}
        onClose={() => setDecorationEditDialog(null)}
        onSaved={handleDecorationSaved}
        assignedQueries={assignedQueries}
        dataQueries={results?.dataQueries}
      />

      <DashboardWidgetEditDialog
        open={!!widgetEditDialog}
        widgetId={widgetEditDialog?.widgetId ?? null}
        titleOverride={widgetEditDialog?.titleOverride ?? ""}
        dateRange={date.range}
        onClose={() => setWidgetEditDialog(null)}
        onSaved={handleWidgetEditSaved}
      />

      <DrillDownDialog
        open={drillDownOpen}
        loading={drillDownLoading}
        data={drillDownData}
        onClose={() => setDrillDownOpen(false)}
      />

      <AppDialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t("share.title")}</AppDialogTitle>
        <AppDialogContent>
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppRadioGroup
                label={t("share.mode")}
                value={shareForm.mode}
                onChange={(value) => setShareForm((f) => ({ ...f, mode: value as "PUBLIC" | "PASSWORD" }))}
                options={[
                  { value: "PUBLIC", label: t("share.public") },
                  { value: "PASSWORD", label: t("share.password") },
                ]}
              />
            </AppFormField>

            {shareForm.mode === "PASSWORD" && (
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  type="password"
                  label={t("share.passwordLabel")}
                  onChange={(e) => setShareForm((f) => ({ ...f, password: e.target.value }))}
                />
              </AppFormField>
            )}

            <AppFormField size={GRID_FULL}>
              <AppAutocomplete<(typeof shareRuntimeOptions)[number]>
                label={t("share.runtime")}
                options={shareRuntimeOptions}
                value={selectedShareRuntime}
                onChange={(_, opt) => {
                  if (!opt) return;
                  setShareForm((f) => ({ ...f, runtimeMode: opt.value }));
                  if (opt.value === "SNAPSHOT") {
                    setShareSnapshotFrom((prev) => prev ?? dayjs(date.range.from));
                    setShareSnapshotTo((prev) => prev ?? dayjs(date.range.to));
                  }
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
              />
            </AppFormField>

            {shareForm.runtimeMode === "SNAPSHOT" && (
              <>
                <AppFormField size={GRID_FULL}>
                  <Alert severity="info">{t("share.snapshotHint")}</Alert>
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <AppDateTimePicker
                      label={t("share.snapshotFrom")}
                      value={shareSnapshotFrom}
                      onChange={(v) => setShareSnapshotFrom(v)}
                      maxDateTime={shareSnapshotTo ?? undefined}
                    />
                  </LocalizationProvider>
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <AppDateTimePicker
                      label={t("share.snapshotTo")}
                      value={shareSnapshotTo}
                      onChange={(v) => setShareSnapshotTo(v)}
                      minDateTime={shareSnapshotFrom ?? undefined}
                    />
                  </LocalizationProvider>
                </AppFormField>
                {shareSnapshotFrom && shareSnapshotTo && shareSnapshotFrom.isAfter(shareSnapshotTo) && (
                  <AppFormField size={GRID_FULL}>
                    <Typography variant="caption" color="error">
                      {t("share.snapshotRangeInvalid")}
                    </Typography>
                  </AppFormField>
                )}
              </>
            )}

            <AppFormField size={GRID_FULL}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <AppDateTimePicker
                  label={t("share.expiresOptional")}
                  value={shareExpiresAt}
                  onChange={(v) => setShareExpiresAt(v)}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="text.secondary">
                {t("share.expiresHint")}
              </Typography>
            </AppFormField>
          </AppFormGrid>

          {shareError && <Alert severity="error">{shareError}</Alert>}

          {lastShareUrl && (
            <Alert severity="success">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={custom.pages.dashboards.shareUrlText}>
                  {lastShareUrl}
                </Typography>
                <AppButton size="small" variant="outlined" onClick={() => copyShareUrl(lastShareUrl)}>
                  {t("share.copyLink")}
                </AppButton>
              </Stack>
            </Alert>
          )}

          {shareLinks.length > 0 && (
            <Box>
              <Divider sx={custom.pages.dashboards.shareLinksDivider} />
              <Typography variant="subtitle2" fontWeight={700} sx={custom.pages.dashboards.shareLinksTitle}>
                {t("share.activeLinks")}
              </Typography>
              {shareLinks.map((l) => {
                const url = `${window.location.origin}/s/${l.token}`;
                return (
                  <Paper
                    key={l.token}
                    variant="outlined"
                    sx={custom.pages.dashboards.shareLinkPaper}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={custom.pages.dashboards.shareLinkUrlBox}>
                        <Typography variant="body2" sx={custom.pages.dashboards.shareLinkMonospace}>
                          {url}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={custom.pages.shared.statChipStack}>
                          {l.runtimeMode === "SNAPSHOT" && (
                            <Chip label={t("share.snapshot")} size="small" variant="outlined" />
                          )}
                          {l.expiresAt && (
                            <Typography variant="caption" color="text.secondary">
                              {t("share.expires")}: {new Date(l.expiresAt).toLocaleString()}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <AppButton size="small" variant="outlined" onClick={() => copyShareUrl(url)}>
                        {t("share.copyLink")}
                      </AppButton>
                      <AppButton
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => revokeShareLink(l.token)}
                      >
                        {t("share.revoke")}
                      </AppButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          )}
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setShareOpen(false)}>{t("common.cancel")}</AppDialogCancelButton>
          <AppDialogPrimaryButton onClick={createShare} disabled={!shareSnapshotValid}>
            {t("common.create")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>
    </DashboardLayout>
  );
}
